// functional-firebase.ts
import { initializeApp } from 'firebase/app'
import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken,
  isSupported,
  Messaging
} from 'firebase/messaging'
import { getAuth, signOut } from 'firebase/auth'
import { setDataToDB } from 'utils/helpers/storageHelper'

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
  publicVapidKey: string
}



const getFirebaseConfig = (): FirebaseConfig => {
  const cleanVapidKey = (process.env.REACT_APP_FIREBASE_PUBLIC_VAPID_KEY || '')
    .replace(/\r?\n/g, '')
    .trim()

  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
    publicVapidKey: cleanVapidKey
  }
}

let appInitialized = false
let messaging: Messaging | null = null
let registration: ServiceWorkerRegistration | null = null

const initializeFirebase = async () => {
  if (appInitialized) return

  const config = getFirebaseConfig()

  if (!config.apiKey || !config.projectId) throw new Error('Firebase config missing')

  initializeApp(config)
  if (await isSupported()) {
    messaging = getMessaging()
  }

  appInitialized = true
}

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return null
  if (registration) return registration

  const existingReg = await navigator.serviceWorker.getRegistration('/')
  if (existingReg) {
    registration = existingReg
  } else {
    const swResponse = await fetch('/firebase-messaging-sw.js')
    if (!swResponse.ok) throw new Error('Service worker file missing')
    registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' })
  }

  try {
    await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('SW ready timeout')), 10000))
    ])
  } catch (e) {
    console.warn(e)
  }

  await sendConfigToServiceWorker()
  return registration
}

const sendConfigToServiceWorker = async () => {
  if (!registration?.active) return
  const config = getFirebaseConfig()
  registration.active.postMessage({ type: 'FIREBASE_CONFIG', config })
}

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  return await Notification.requestPermission()
}

export const getFCMToken = async (): Promise<string | null> => {
  await initializeFirebase()
  if (!messaging) return null
  const reg = await registerServiceWorker()
  if (!reg) return null

  const config = getFirebaseConfig()
  const token = await getToken(messaging, {
    vapidKey: config.publicVapidKey,
    serviceWorkerRegistration: reg
  })

  if (token) {
    await setDataToDB('vimesoft', 'fcmToken', token)
    return token
  }
  return null
}

export const deleteFCMToken = async (): Promise<void> => {
  await initializeFirebase()
  if (messaging) {
    await deleteToken(messaging)
    await setDataToDB('vimesoft', 'fcmToken', null)
  }
}

export const signOutFirebase = async (): Promise<void> => {
  await initializeFirebase()
  const auth = getAuth()
  await signOut(auth)
  await unregisterServiceWorker()
  await deleteFCMToken()
}

export const unregisterServiceWorker = async (): Promise<void> => {
  if (registration) {
    await registration.unregister()
    registration = null
  }
}

export const onMessageListener = (callback: (payload: any) => void) => {
  if (messaging) onMessage(messaging, callback)
}

export const initializeNotifications = async () => {
  const permission = await requestNotificationPermission()
  if (permission === 'granted') {
    await registerServiceWorker()
    await getFCMToken()
  }
}

export const isNotificationSupported = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator
}

export const getNotificationPermission = (): NotificationPermission => {
  return Notification.permission
}
